import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useProfile } from '../context/ProfileContext';
import './MatchDetail.css';
 
const MatchDetail = () => {
  const { matchId } = useParams();

  const { profile } = useProfile();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [questions, setQuestions] = useState([]);
  const [editable, setEditable] = useState(false);

  useEffect(() => {
    const fetchMatchDetails = async () => {
      try {
        setLoading(true);
        // Fetch the specific match details
        console.log('Fetching match details for ID:', matchId);
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/match-detail/${matchId}`);
        console.log('API response:', response.data);

        // Set match data
        setMatch(response.data.match);

        // Handle questions data with proper checks
        if (response.data.questions && Array.isArray(response.data.questions)) {
          console.log('Setting questions from API:', response.data.questions);
          setQuestions(response.data.questions);
        } else {
          console.log('No valid questions data in response, setting empty array');
          setQuestions([]);
        }

        // Initialize selected options from user's previous choices
        if (response.data.userSelections) {
          console.log('Setting user selections:', response.data.userSelections);
          setSelectedOptions(response.data.userSelections);
        }

        // Set editable status based on match status
        setEditable(
          response.data.match &&
          (response.data.match.status === 'upcoming' || response.data.match.status === 'upcomming')
        );

        setLoading(false);
      } catch (err) {
        console.error('Error fetching match details:', err);
        setError('Failed to load match details. Please try again later.');
        setLoading(false);
      }
    };

    if (matchId) {
      fetchMatchDetails();
    }
  }, [matchId]);

  // const handleOptionChange = (questionId, optionId) => {
  //   if (!editable) return;

  //   setSelectedOptions({
  //     ...selectedOptions,
  //     [questionId]: optionId
  //   });
  // };

  const handleSaveChanges = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/api/update-selections`, {
        matchId,
        userId: profile.userId,
        selections: selectedOptions
      });

      if (response.data.success) {
        alert('Your selections have been updated successfully!');
      } else {
        alert('Failed to update selections. Please try again.');
      }
    } catch (err) {
      console.error('Error updating selections:', err);
      alert('An error occurred while updating your selections.');
    }
  };

  const renderQuestions = () => {
    if (!match) return null;
    console.log('Rendering questions. Current questions state:', questions);

    // Standardized question rendering for both parlay and 4ka100
    return (
      <div className="match-detail-quiz-container">
        <div className="match-detail-potential-profit">
          <span className="match-detail-profit-label">Potential Profit:</span>
          <span className="match-detail-profit-amount">₹{match.profit || 1300}</span>
        </div>
        
        <div className="match-detail-selections-header">
          <h2>Your Selections</h2>
        </div>

        <div className="match-detail-questions-list">
          {!questions || questions.length === 0 ? (
            // Show default questions when no data available
            <>
              <div className="match-detail-question-item">
                <div className="match-detail-question-label">a</div>
                <div className="match-detail-question-content">
                  <div className="match-detail-question-text">No options available for this question</div>
                </div>
              </div>
              <div className="match-detail-question-item">
                <div className="match-detail-question-label">b</div>
                <div className="match-detail-question-content">
                  <div className="match-detail-question-text">No options available for this question</div>
                </div>
              </div>
              <div className="match-detail-question-item">
                <div className="match-detail-question-label">c</div>
                <div className="match-detail-question-content">
                  <div className="match-detail-question-text">No options available for this question</div>
                </div>
              </div>
              <div className="match-detail-question-item">
                <div className="match-detail-question-label">d</div>
                <div className="match-detail-question-content">
                  <div className="match-detail-question-text">No options available for this question</div>
                </div>
              </div>
            </>
          ) : (
            questions.map((question, index) => {
              const questionId = question._id || `question_${index}`;
              const labels = ['a', 'b', 'c', 'd'];
              
              if (match.betType === 'parlay') {
                // For parlay - show selected rules in one row
                return (
                  <div key={questionId} className="match-detail-question-item selected">
                    <div className="match-detail-question-label">{labels[index] || index + 1}</div>
                    <div className="match-detail-question-content">
                      <div className="match-detail-question-text">{question.text || question.question || 'Selected rule'}</div>
                      <div className="match-detail-question-odds">Odds: {question.options?.[0]?.odds ?? '1'}</div>
                      <div className="match-detail-question-status">✓</div>
                    </div>
                  </div>
                );
              } else {
                // For 4ka100 - show options if available
                const hasValidOptions = question.options && Array.isArray(question.options);
                const isSelected = selectedOptions[questionId];
                
                if (hasValidOptions && isSelected) {
                  // Show selected option in one row
                  const selectedOption = question.options.find(opt => (opt._id || opt) === selectedOptions[questionId]);
                  const selectedText = selectedOption ? (typeof selectedOption === 'string' ? selectedOption : selectedOption.text) : question.text || question.question;
                  
                  return (
                    <div key={questionId} className="match-detail-question-item selected">
                      <div className="match-detail-question-label">{labels[index] || index + 1}</div>
                      <div className="match-detail-question-content">
                        <div className="match-detail-question-text">{selectedText}</div>
                        <div className="match-detail-question-odds">Odds: {selectedOption?.odds ?? '2'}</div>
                        <div className="match-detail-question-status">✓</div>
                      </div>
                    </div>
                  );
                } else {
                  // Show question without selection
                  return (
                    <div key={questionId} className="match-detail-question-item">
                      <div className="match-detail-question-label">{labels[index] || index + 1}</div>
                      <div className="match-detail-question-content">
                        <div className="match-detail-question-text">{question.text || question.question || 'Question text'}</div>
                      <div className="match-detail-question-odds">Selected: {question.selectedOption}</div>
                      </div>
                    </div>
                  );
                }
              }
            })
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="match-detail-loading">Loading match details...</div>;
  }

  if (error) {
    return <div className="match-detail-error">{error}</div>;
  }

  if (!match) {
    return <div className="match-detail-error">Match not found</div>;
  }

  return (
    <div className="match-detail-page">
      {/* Header Section */}
      <header className="match-detail-header">
        <div className="match-detail-header-left">
          <h1 className="match-detail-site-title">CricWhizz</h1>
        </div>
        <div className="match-detail-header-right">
          <div className="match-detail-wallet-button">
            <span className="match-detail-wallet-icon">💰</span>
            <span className="match-detail-wallet-amount">{profile.walletBalance}</span>
          </div>
        </div>
      </header>

      {/* Match Card */}
      <div className="match-detail-card">
        <div className="match-detail-status-container">
          <div className={`match-detail-status-badge ${match.status}`}>
            {match.status.toUpperCase()}
          </div>
        </div>
        
        <div className="match-detail-teams-container">
          <div className="match-detail-team-section">
            <div className="match-detail-team-name">{match.team1}</div>
        
          </div>
          
          <div className="match-detail-vs-separator">VS</div>
          
          <div className="match-detail-team-section">
            <div className="match-detail-team-name">{match.team2}</div>
     
          </div>
          
          <div className="match-detail-time">{match.time}</div>
        </div>
      </div>

      {/* Questions/Quiz Section */}
      {renderQuestions()}

      {/* Save Changes Button (only for upcoming matches) */}
      {editable && (
        <div className="match-detail-actions-container">
          <button className="match-detail-save-changes-button" onClick={handleSaveChanges}>
            Edit Response
          </button>
        </div>
      )}
    </div>
  );
};

export default MatchDetail; 
